import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { FiX, FiDelete } from 'react-icons/fi';
import { colors, borderRadius, typography, shadows } from '../../theme/colors';

/**
 * Touch-friendly calculator-style modal used by the POS cart to edit qty,
 * price, and per-line discount. Supports basic 4-op math so a cashier can
 * type "50+30" and hit Enter to apply 80.
 *
 * Props:
 *   open        boolean
 *   title       string — header line (e.g. "Qty for Coca Cola")
 *   subtitle    string — optional second line (e.g. item name)
 *   field       'qty' | 'price' | 'discount'  — drives the bottom hint
 *   initialValue number
 *   initialMode 'amount' | 'percent'           — only for discount field
 *   max         number — optional cap (e.g. 100 for percent)
 *   onConfirm   (value: number, mode?: 'amount'|'percent') => void
 *   onClose     () => void
 */
const POSCalculatorModal = ({
  open,
  title,
  subtitle,
  field = 'qty',
  initialValue = 0,
  initialMode = 'amount',
  max = null,
  onConfirm,
  onClose,
}) => {
  // Calculator state. We render `display` directly; accum/pendingOp track
  // pending math operations so we evaluate left-to-right on each operator
  // and again on '=' / Enter (calculator-style, not RPN).
  const [display, setDisplay] = useState('0');
  const [accum, setAccum] = useState(null);
  const [pendingOp, setPendingOp] = useState(null);
  const [justEvaluated, setJustEvaluated] = useState(false);
  const [mode, setMode] = useState(initialMode);

  // Reset state every time the modal opens
  useEffect(() => {
    if (!open) return;
    const init = Number.isFinite(Number(initialValue)) ? Number(initialValue) : 0;
    setDisplay(init === 0 ? '0' : String(init));
    setAccum(null);
    setPendingOp(null);
    setJustEvaluated(true); // first digit replaces, doesn't append
    setMode(initialMode || 'amount');
  }, [open, initialValue, initialMode]);

  const currentNumeric = useMemo(() => {
    const n = Number(display);
    return Number.isFinite(n) ? n : 0;
  }, [display]);

  // ─── Action handlers ───────────────────────────────────────────────────

  const pressDigit = useCallback((d) => {
    setDisplay(prev => {
      const start = justEvaluated ? '' : (prev === '0' ? '' : prev);
      setJustEvaluated(false);
      const next = start + d;
      // Soft cap on length for sanity (prevents the field overflowing)
      return next.length > 12 ? prev : next;
    });
  }, [justEvaluated]);

  const pressDot = useCallback(() => {
    setDisplay(prev => {
      if (justEvaluated) { setJustEvaluated(false); return '0.'; }
      return prev.includes('.') ? prev : prev + '.';
    });
  }, [justEvaluated]);

  const pressBackspace = useCallback(() => {
    setDisplay(prev => {
      if (justEvaluated) { setJustEvaluated(false); return '0'; }
      const next = prev.slice(0, -1);
      return next === '' || next === '-' ? '0' : next;
    });
  }, [justEvaluated]);

  const pressClear = useCallback(() => {
    setDisplay('0');
    setAccum(null);
    setPendingOp(null);
    setJustEvaluated(true);
  }, []);

  const evaluate = useCallback((a, op, b) => {
    if (op === '+') return a + b;
    if (op === '-') return a - b;
    if (op === '×' || op === '*') return a * b;
    if (op === '÷' || op === '/') return b === 0 ? 0 : a / b;
    return b;
  }, []);

  const pressOp = useCallback((op) => {
    const cur = currentNumeric;
    if (accum !== null && pendingOp && !justEvaluated) {
      const res = evaluate(accum, pendingOp, cur);
      setAccum(res);
      setDisplay(formatResult(res));
    } else {
      setAccum(cur);
    }
    setPendingOp(op);
    setJustEvaluated(true);
  }, [currentNumeric, accum, pendingOp, justEvaluated, evaluate]);

  const pressEquals = useCallback(() => {
    if (accum === null || !pendingOp) return currentNumeric;
    const res = evaluate(accum, pendingOp, currentNumeric);
    setDisplay(formatResult(res));
    setAccum(null);
    setPendingOp(null);
    setJustEvaluated(true);
    return res;
  }, [accum, pendingOp, currentNumeric, evaluate]);

  const handleConfirm = useCallback(() => {
    let v;
    if (accum !== null && pendingOp) {
      v = evaluate(accum, pendingOp, currentNumeric);
    } else {
      v = currentNumeric;
    }
    if (v < 0) v = 0;
    if (max != null && v > max) v = max;
    // Quantity is treated as an integer-ish positive number; allow decimals
    // (some businesses sell by weight). For percent, cap at 100.
    onConfirm?.(v, mode);
  }, [accum, pendingOp, currentNumeric, max, onConfirm, mode, evaluate]);

  // ─── Physical keyboard support ────────────────────────────────────────

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      // Don't swallow when the user is in another input (shouldn't happen with
      // a modal but defensive). Modal traps focus anyway.
      const k = e.key;
      if (/^[0-9]$/.test(k)) { e.preventDefault(); pressDigit(k); return; }
      if (k === '.') { e.preventDefault(); pressDot(); return; }
      if (k === 'Backspace') { e.preventDefault(); pressBackspace(); return; }
      if (k === 'Escape') { e.preventDefault(); onClose?.(); return; }
      if (k === 'Enter' || k === '=') { e.preventDefault(); handleConfirm(); return; }
      if (k === '+') { e.preventDefault(); pressOp('+'); return; }
      if (k === '-') { e.preventDefault(); pressOp('-'); return; }
      if (k === '*' || k === 'x' || k === 'X') { e.preventDefault(); pressOp('×'); return; }
      if (k === '/') { e.preventDefault(); pressOp('÷'); return; }
      if (k === 'c' || k === 'C') { e.preventDefault(); pressClear(); return; }
    };
    window.addEventListener('keydown', handler, true);
    return () => window.removeEventListener('keydown', handler, true);
  }, [open, pressDigit, pressDot, pressBackspace, onClose, handleConfirm, pressOp, pressClear]);

  if (!open) return null;

  const showModeTabs = field === 'discount';
  const displaySuffix = field === 'discount' && mode === 'percent' ? ' %' : '';
  const hint = field === 'qty'
    ? 'Enter quantity'
    : field === 'price'
      ? 'Enter unit price'
      : (mode === 'percent' ? 'Enter discount percent (0–100)' : 'Enter discount amount');

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <Header>
          <Titles>
            <Title>{title}</Title>
            {subtitle && <Subtitle>{subtitle}</Subtitle>}
          </Titles>
          <CloseBtn onClick={onClose}><FiX size={22} /></CloseBtn>
        </Header>

        {showModeTabs && (
          <ModeTabs>
            <ModeTab $active={mode === 'amount'} onClick={() => setMode('amount')}>Amount</ModeTab>
            <ModeTab $active={mode === 'percent'} onClick={() => setMode('percent')}>Percent</ModeTab>
          </ModeTabs>
        )}

        <Display>
          <DisplayHint>{hint}</DisplayHint>
          <DisplayValue>{display}{displaySuffix}</DisplayValue>
          {accum !== null && pendingOp && (
            <DisplayPending>{formatResult(accum)} {pendingOp}</DisplayPending>
          )}
        </Display>

        <Grid>
          <Btn onClick={() => pressDigit('7')}>7</Btn>
          <Btn onClick={() => pressDigit('8')}>8</Btn>
          <Btn onClick={() => pressDigit('9')}>9</Btn>
          <Btn $kind="op" onClick={() => pressOp('÷')}>÷</Btn>

          <Btn onClick={() => pressDigit('4')}>4</Btn>
          <Btn onClick={() => pressDigit('5')}>5</Btn>
          <Btn onClick={() => pressDigit('6')}>6</Btn>
          <Btn $kind="op" onClick={() => pressOp('×')}>×</Btn>

          <Btn onClick={() => pressDigit('1')}>1</Btn>
          <Btn onClick={() => pressDigit('2')}>2</Btn>
          <Btn onClick={() => pressDigit('3')}>3</Btn>
          <Btn $kind="op" onClick={() => pressOp('-')}>−</Btn>

          <Btn onClick={() => pressDigit('0')}>0</Btn>
          <Btn onClick={pressDot}>.</Btn>
          <Btn $kind="clear" onClick={pressClear}>C</Btn>
          <Btn $kind="op" onClick={() => pressOp('+')}>+</Btn>

          <Btn $kind="back" onClick={pressBackspace}><FiDelete size={20} /></Btn>
          <Btn $kind="eq" onClick={() => pressEquals()}>=</Btn>
          <EnterBtn onClick={handleConfirm}>ENTER ⏎</EnterBtn>
        </Grid>
      </Modal>
    </Overlay>
  );
};

const formatResult = (n) => {
  if (!Number.isFinite(n)) return '0';
  // Trim trailing zeros while keeping decimals when present.
  if (Number.isInteger(n)) return String(n);
  const rounded = Math.round(n * 1e6) / 1e6;
  return String(rounded);
};

// ─── Styled ──────────────────────────────────────────────────────────────

const Overlay = styled.div`
  position: fixed; inset: 0;
  background: rgba(0,0,0,0.55);
  display: flex; align-items: center; justify-content: center;
  z-index: 1200;
  animation: fadeIn 120ms ease-out;
  @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
`;
const Modal = styled.div`
  width: 380px; max-width: 95vw;
  background: white;
  border-radius: ${borderRadius.lg};
  box-shadow: ${shadows.xl};
  display: flex; flex-direction: column;
  overflow: hidden;
`;
const Header = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 16px;
  background: ${colors.secondary.main};
  color: white;
`;
const Titles = styled.div`
  display: flex; flex-direction: column; gap: 2px;
`;
const Title = styled.div`
  font-size: ${typography.fontSize.md};
  font-weight: ${typography.fontWeight.bold};
  letter-spacing: 0.5px;
`;
const Subtitle = styled.div`
  font-size: ${typography.fontSize.xs};
  opacity: 0.8;
`;
const CloseBtn = styled.button`
  background: transparent;
  border: 1px solid rgba(255,255,255,0.3);
  border-radius: ${borderRadius.sm};
  color: white;
  width: 32px; height: 32px;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer;
  &:hover { background: rgba(255,255,255,0.15); }
`;
const ModeTabs = styled.div`
  display: flex;
  padding: 8px 12px 0;
  gap: 6px;
  background: white;
`;
const ModeTab = styled.button`
  flex: 1;
  padding: 8px 0;
  border-radius: ${borderRadius.sm};
  border: 2px solid ${({ $active }) => $active ? colors.primary.main : colors.border.light};
  background: ${({ $active }) => $active ? colors.primary.main + '15' : 'white'};
  color: ${({ $active }) => $active ? colors.primary.main : colors.text.secondary};
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.bold};
  letter-spacing: 0.5px;
  text-transform: uppercase;
  cursor: pointer;
`;
const Display = styled.div`
  padding: 16px 18px;
  background: #0F172A;
  color: white;
  display: flex; flex-direction: column; align-items: flex-end;
  gap: 4px;
  margin: 12px;
  border-radius: ${borderRadius.md};
  min-height: 90px;
`;
const DisplayHint = styled.div`
  align-self: flex-start;
  font-size: ${typography.fontSize.xs};
  opacity: 0.55;
  letter-spacing: 0.5px;
  text-transform: uppercase;
`;
const DisplayValue = styled.div`
  font-family: ${typography.fontFamily.mono};
  font-size: 38px;
  font-weight: ${typography.fontWeight.bold};
  line-height: 1.1;
  word-break: break-all;
  text-align: right;
`;
const DisplayPending = styled.div`
  font-size: ${typography.fontSize.xs};
  opacity: 0.55;
`;
const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 6px;
  padding: 0 12px 14px;
`;
const Btn = styled.button`
  padding: 14px 0;
  font-size: 20px;
  font-weight: ${typography.fontWeight.bold};
  border-radius: ${borderRadius.sm};
  border: 1px solid ${colors.border.light};
  cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  background: ${({ $kind }) => {
    switch ($kind) {
      case 'op':    return '#EEF2FF';
      case 'eq':    return '#FFFBEB';
      case 'clear': return '#FEE2E2';
      case 'back':  return '#FEF3C7';
      default:      return 'white';
    }
  }};
  color: ${({ $kind }) => {
    switch ($kind) {
      case 'op':    return '#4338CA';
      case 'eq':    return '#B45309';
      case 'clear': return '#B91C1C';
      case 'back':  return '#92400E';
      default:      return colors.text.primary;
    }
  }};
  transition: filter 0.08s;
  &:hover { filter: brightness(0.95); }
  &:active { transform: scale(0.97); }
`;
const EnterBtn = styled.button`
  grid-column: span 2;
  padding: 14px 0;
  font-size: ${typography.fontSize.lg};
  font-weight: ${typography.fontWeight.bold};
  letter-spacing: 1px;
  background: ${colors.success.main};
  color: white;
  border: none;
  border-radius: ${borderRadius.sm};
  cursor: pointer;
  display: flex; align-items: center; justify-content: center; gap: 6px;
  &:hover { filter: brightness(0.92); }
  &:active { transform: scale(0.97); }
`;

export default POSCalculatorModal;
